import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Box, IconButton, LinearProgress, Tooltip } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Bytes';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const AudioPlayer = ({ audioUrl, fileSize }) => {
  const { t } = useTranslation();

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPct, setPlaybackPct] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isSeeking, setIsSeeking] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const d = audioRef.current.duration || 0;
    const c = audioRef.current.currentTime || 0;
    if (d > 0) setPlaybackPct(Math.min(100, Math.max(0, (c / d) * 100)));
    setDuration(d || 0);
    setCurrentTime(c || 0);
  };

  const seekToClientX = (clientX) => {
    if (!progressRef.current || !audioRef.current || duration <= 0) return;
    const rect = progressRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const newTime = ratio * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setPlaybackPct(ratio * 100);
  };

  const handleSeekStart = (e) => {
    e.preventDefault();
    setIsSeeking(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    seekToClientX(clientX);
  };

  const handleSeekMove = (e) => {
    if (!isSeeking) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    seekToClientX(clientX);
  };

  const handleSeekEnd = () => {
    if (!isSeeking) return;
    setIsSeeking(false);
  };

  const displaySize = fileSize ? formatBytes(fileSize) : '';

  if (!audioUrl) return null;

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: '#F5F5F5',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Tooltip title={isPlaying ? t('pause') : t('play')}>
          <span>
            <IconButton
              onClick={handlePlayPause}
              sx={{
                bgcolor: '#0F0F0F',
                color: '#fff',
                '&:hover': { bgcolor: '#404040' },
                width: 40,
                height: 40,
                p: 0,
              }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontFamily: 'Readex Pro',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              letterSpacing: '0.64px',
              color: '#0F0F0F',
            }}
          >
            {t('audio_intro_about_artwork')}
          </Box>
          <Box
            sx={{
              fontFamily: 'Readex Pro',
              fontWeight: 200,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.56px',
              color: '#404040',
              mt: 0.5,
            }}
          >
            {displaySize || ''}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: 24 }}>
        <Box
          sx={{
            fontFamily: 'Readex Pro',
            fontWeight: 200,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.56px',
            color: '#404040',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {formatTime(currentTime)}
        </Box>
        <Box
          ref={progressRef}
          sx={{ flex: 1, minWidth: 0, cursor: 'pointer', userSelect: 'none' }}
          onMouseDown={handleSeekStart}
          onMouseMove={handleSeekMove}
          onMouseUp={handleSeekEnd}
          onMouseLeave={handleSeekEnd}
          onTouchStart={handleSeekStart}
          onTouchMove={handleSeekMove}
          onTouchEnd={handleSeekEnd}
        >
          <LinearProgress
            variant="determinate"
            value={playbackPct}
            sx={{
              height: 8,
              borderRadius: '999px',
              backgroundColor: '#E5E5E5',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#0F0F0F',
                borderRadius: '999px',
              },
            }}
          />
        </Box>
        <Box
          sx={{
            fontFamily: 'Readex Pro',
            fontWeight: 200,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.56px',
            color: '#404040',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {formatTime(duration)}
        </Box>
        <Tooltip title={muted ? t('unmute') : t('mute')}>
          <span>
            <IconButton
              onClick={() => {
                if (!audioRef.current) return;
                const next = !muted;
                setMuted(next);
                audioRef.current.muted = next;
              }}
              sx={{ width: 24, height: 24, p: 0 }}
            >
              {muted ? (
                <VolumeOffIcon sx={{ fontSize: 24 }} />
              ) : (
                <VolumeUpIcon sx={{ fontSize: 24 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => {
          if (audioRef.current) {
            setDuration(audioRef.current.duration || 0);
          }
        }}
        onEnded={() => setIsPlaying(false)}
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default AudioPlayer;
